import { useEffect, useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { supabase } from "../lib/supabase";
import { STATUS_TABS } from "./seller-products/constants";
import { ProductRow } from "./seller-products/ProductRow";
import { ProductForm } from "./seller-products/ProductForm";

export function SellerProducts({ seller }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  // drawer: { mode: "create" } | { mode: "edit", product }
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    if (!seller?.id) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select(
          "id, title, description, price, compare_price, cost_price, stock_qty, low_stock_threshold, images, status, total_sold, view_count, created_at, sizes, attributes, category_id, category:categories(name)"
        )
        .eq("seller_id", seller.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      setProducts(data || []);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [seller?.id]);

  const visible = useMemo(() => {
    return products.filter((p) => {
      if (tab === "active" && p.status !== "active") return false;
      if (tab === "paused" && p.status !== "paused") return false;
      if (tab === "low_stock") {
        if (p.stock_qty === 0) return false;
        if (p.stock_qty > (p.low_stock_threshold ?? 5)) return false;
      }
      if (tab === "out" && p.stock_qty !== 0) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        if (!p.title?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [products, tab, query]);

  function patchLocal(id, patch) {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    setDrawer((d) =>
      d && d.mode === "edit" && d.product.id === id ? { ...d, product: { ...d.product, ...patch } } : d
    );
  }

  function removeLocal(id) {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setDrawer(null);
  }

  function addLocal(product) {
    setProducts((prev) => [product, ...prev]);
  }

  const selectedId = drawer?.mode === "edit" ? drawer.product.id : null;

  if (!seller) {
    return <EmptyState icon="store" title="No seller account" body="Seller products require a seller profile." />;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: drawer ? "1.4fr 1fr" : "1fr", gap: 18 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <Eyebrow>Catalog</Eyebrow>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.5px" }}>
              Products
            </h1>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>
              {products.length} total · {products.filter((p) => p.status === "active").length} active
            </div>
          </div>
          <Btn icon="plus" onClick={() => setDrawer({ mode: "create" })}>
            Add product
          </Btn>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              background: C.white,
              borderRadius: 12,
              border: "1px solid rgba(196,193,183,0.3)",
            }}
          >
            <Icon name="search" size={14} color={C.muted} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your products…"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: 13,
                fontFamily: "inherit",
                background: "transparent",
                color: C.charcoal,
              }}
            />
          </div>
          <div style={{ display: "inline-flex", padding: 3, borderRadius: 12, background: C.cream, gap: 2 }}>
            {STATUS_TABS.map((t) => {
              const on = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    border: "none",
                    background: on ? C.white : "transparent",
                    color: on ? C.hunter : C.muted,
                    fontSize: 11.5,
                    fontWeight: 800,
                    padding: "6px 10px",
                    borderRadius: 9,
                    cursor: "pointer",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div style={{ display: "grid", gap: 10 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={72} />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon="tag"
            title={products.length === 0 ? "No products yet" : "No products match your filter"}
            body={
              products.length === 0
                ? "Tap \"Add product\" to list your first item."
                : "Try a different filter or search."
            }
          />
        ) : (
          <Card pad={0}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "48px 2fr 1fr 1fr 1fr 0.8fr",
                gap: 12,
                padding: "12px 16px",
                fontSize: 10.5,
                fontWeight: 800,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: 1,
                borderBottom: `1px solid ${C.cream}`,
              }}
            >
              <span></span>
              <span>Product</span>
              <span>Price</span>
              <span>Stock</span>
              <span>Sold</span>
              <span>Status</span>
            </div>
            {visible.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                selected={selectedId === p.id}
                onOpen={(product) => setDrawer({ mode: "edit", product })}
              />
            ))}
          </Card>
        )}
      </div>

      {drawer && (
        <ProductForm
          key={drawer.mode === "edit" ? drawer.product.id : "create"}
          mode={drawer.mode}
          product={drawer.mode === "edit" ? drawer.product : null}
          seller={seller}
          onClose={() => setDrawer(null)}
          onPatch={patchLocal}
          onCreated={addLocal}
          onDeleted={removeLocal}
        />
      )}
    </div>
  );
}
